import { useState } from "react";
import { Formik, useFormik } from "formik";
import { isNil } from 'ramda';
import { useDispatch } from "redux-react-hook";
import { refetchCurrentUser } from "store/DEPRECATEDusers/asyncAction";
import styled from "styled-components";
import { ICharacter } from "types/characters";
import { errorAlert, successAlert } from 'ui-kit/Alert';

import { useGenerateCharacterPhotoMutation, useRateGeneratedPhotoMutation } from "../../services/api/charactersApi";
import Modal from "../../ui-kit/Modal";
import GenerationStep from "./components/GenerationStep";
import ViewGeneratedPhotoStep from "./components/ViewGeneratedPhotoStep";
import {
  attributesToGeneratePhoto,
  attributesToRateGeneratedPhoto,
  initialValues,
  IPhotoGeneratorFormValues,
  IRateGeneratedPhotoFormValues
} from './form';

interface IPhotoGeneratorModalProps {
  isOpen: boolean;
  onClose: () => void;
  character: ICharacter;
  refetchCharacter: () => Promise<void>;
  onOpenSubscriptionModal: () => void;
  openBuyTokensModal: () => void;
}

interface IGeneratedImage {
  photoId: string;
  url: string;
}

const GENERATION_STEP = 0;
const VIEW_GENERATED_PHOTO_STEP = 1;

const PhotoGeneratorModal: React.FC<IPhotoGeneratorModalProps> = (props) => {
  const { isOpen, onClose, character, refetchCharacter, onOpenSubscriptionModal, openBuyTokensModal } = props;

  const [step, setStep] = useState(GENERATION_STEP);
  const [generatedImage, setGeneratedImage] = useState<IGeneratedImage | null>(null);
  const dispatch = useDispatch();


  const [generateCharacterPhoto, { isLoading: isLoadingGeneratingPhoto }] = useGenerateCharacterPhotoMutation();
  const [rateGeneratedPhoto] = useRateGeneratedPhotoMutation();

  const handleSubmit = async (values: IPhotoGeneratorFormValues) => {
    const attributes = attributesToGeneratePhoto(values);
    try {
      const data = await generateCharacterPhoto({ id: character.id, params: attributes }).unwrap();
      setGeneratedImage(data);
      dispatch(refetchCurrentUser());
      setStep(VIEW_GENERATED_PHOTO_STEP);
      successAlert('Photo generated successfully');
    } catch (error) {
      errorAlert('Failed to generate photo, please try again');
      setStep(GENERATION_STEP);
      throw error;
    }
  };

  const handleClose = async () => {
    if (!isNil(generatedImage)) {
      await refetchCharacter();
    }
    setStep(0);
    onClose();
  };

  const handleFeedback = async (values: IRateGeneratedPhotoFormValues): Promise<void> => {
    try {
      const attributes = attributesToRateGeneratedPhoto(values);
      if (generatedImage?.photoId) {
        await rateGeneratedPhoto({ characterId: character.id, photoId: generatedImage.photoId, params: attributes }).unwrap();
      }
      successAlert('Thanks for your message!');
      handleClose();
    } catch (error) {
      errorAlert('Failed to send feedback, please try again');
      setStep(VIEW_GENERATED_PHOTO_STEP);
      throw error;
    }
  };

  const isPhotoGenerationStep = step === GENERATION_STEP;
  const isViewGeneratedPhotoStep = step === VIEW_GENERATED_PHOTO_STEP;

  const formProps = useFormik({
    initialValues,
    onSubmit: handleSubmit
  });

  const handleCloseModal = () => {
    formProps.resetForm();
    setStep(GENERATION_STEP);
    handleClose();
    setGeneratedImage(null);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <Modal isOpen={isOpen} onClose={handleCloseModal} canClose={true}>
      <Wrapper step={step}>
        <>
          <Header>
            <Title>Create photo</Title>
          </Header>
          <>
            <Formik
              initialValues={initialValues}
              onSubmit={handleSubmit}
            >
              {({ values, setFieldValue, handleSubmit }) => (
                <>
                  {isPhotoGenerationStep && (
                    <GenerationStep
                      isGenerationLoading={isLoadingGeneratingPhoto}
                      values={values}
                      character={character}
                      setFieldValue={setFieldValue}
                      onOpenSubscriptionModal={onOpenSubscriptionModal}
                      handleSubmit={handleSubmit}
                      openBuyTokensModal={openBuyTokensModal}
                    />
                  )}
                  {isViewGeneratedPhotoStep && generatedImage?.url && (
                    <ViewGeneratedPhotoStep
                      values={values}
                      setFieldValue={setFieldValue}
                      generatedImage={generatedImage.url}
                      onFeedback={handleFeedback} />
                  )}
                </>
              )}
            </Formik>
          </>
        </>
      </Wrapper>
    </Modal>
  );
};

export default PhotoGeneratorModal;

const Wrapper = styled.div<{ step: number }>`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 75vh;
  width: 65vw;
  max-width: ${({ step }) => (step === GENERATION_STEP ? "420px" : "590px")};

  @media (max-width: 768px) {
    height: 90vh;
    width: 100%;
  }

  @media (max-height: 890px) {
    height: 85vh;
  }

  @media (max-height: 740px) {
    height: 96vh;
  }

  @media (max-width: 400px) { 
    height: 85vh;
  }
`;

const Header = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  margin-bottom: 20px;
`;

const Title = styled.h2`
  color: #fff;
  font-family: Raleway;
  font-size: 20px;
  font-weight: 700;
  line-height: 22px;
  text-align: center;
`;
